/**
 * Conciliación técnica de un estado: la estructura `B*` del mapping.
 *
 * NO es el estado financiero oficial —ese vive en `/closing`, con el formato del
 * contador y su pie de firmas—. Esta pantalla muestra el andamiaje con el que el
 * mapping se reconcilia contra el documento: la jerarquía `B1 › B11 › B1101`, el
 * coeficiente de cada renglón y la marca de los que corrigen el reporte de
 * origen.
 *
 * Las cifras finales coinciden con el estado oficial por regla BLOQUEANTE
 * (`esf_presentation_matches_statement`); la estructura no, y no debe. Cuando
 * esta pantalla se llamaba «Estado de Situación Financiera» en el menú, esa
 * distinción era invisible y el contador tenía dos documentos donde hay uno.
 *
 * La jerarquía se dibuja por indentación desde `parent_line_id`; los renglones
 * que vienen de una corrección quedan marcados, porque son precisamente los que
 * explican por qué la cifra difiere del reporte del sistema de origen.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import { useAudit } from "./auditContext";
import { PeriodPicker } from "../components/PeriodPicker";
import { defaultPeriod } from "../components/period";
import { ErrorState, Loading } from "../components/States";
import { StatusBadge } from "../components/StatusBadge";
import { formatMoney, moneyClass } from "../components/format";
import { ExportMenu } from "../components/ExportMenu";

interface Props {
  statementKey: "statement_financial_position" | "income_statement";
  reportKey: string;
  /** ESF o ERI: el estado oficial con el que esta vista se reconcilia. */
  official: string;
}

export function Statement({ statementKey, reportKey, official }: Props) {
  const { config } = useAudit();
  const [period, setPeriod] = useState(defaultPeriod);
  const names = config.mapping.statements[statementKey] ?? [];
  const { data, error, loading } = useAsync(
    () => api.statement(statementKey, period),
    [statementKey, period.periodFrom, period.periodTo],
  );

  const corrections = (data ?? []).filter((line) => line.origin === "correction");

  return (
    <>
      {/* Que esto NO es el entregable se dice antes de la primera cifra, no
          al pie: quien llega por un enlace directo tiene que saberlo antes de
          copiar un importe. */}
      <div className="card banner">
        <StatusBadge tone="neutral">Vista técnica</StatusBadge>
        <div className="banner-body">
          <strong>
            Conciliación técnica · {official} — no es el estado financiero oficial
          </strong>
          <p className="small muted">
            Muestra la estructura <span className="mono">B*</span> con la que el
            mapping se reconcilia contra el estado oficial
            {names[0] ? ` («${names[0]}»)` : ""}. Las cifras finales coinciden con
            el estado oficial por regla bloqueante; la estructura no.{" "}
            <Link to="/closing">Ver el estado oficial</Link>.
          </p>
        </div>
      </div>


      <PeriodPicker value={period} onChange={setPeriod}>
        <ExportMenu
          documento={{
            label: `Conciliación técnica · ${official}`,
            note: "La estructura B* con la que el mapping se reconcilia, no el estado oficial.",
            xlsx: api.downloadUrl(reportKey, "xlsx", period),
            pdf: api.downloadUrl(reportKey, "pdf", period),
          }}
        />
      </PeriodPicker>

      {corrections.length > 0 && (
        <div className="card banner">
          <StatusBadge tone="serious">Corregido</StatusBadge>
          <div className="banner-body">
            <strong>{corrections.length} renglones corrigen el reporte de origen</strong>
            <ul className="small muted" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {corrections.map((line) => (
                <li key={line.line_id}>
                  <span className="mono">{line.line_id}</span> {line.label}
                  {line.correction_reason ? ` — ${line.correction_reason}` : ""}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {loading && <Loading what="la conciliación técnica" />}
      {error && <ErrorState error={error} />}

      {data && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Línea</th>
                <th scope="col">Concepto</th>
                <th scope="col" className="num">
                  Movimiento
                </th>
                <th scope="col" className="num">
                  Saldo
                </th>
                <th scope="col">Trazabilidad</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Sin renglones para el período seleccionado.
                  </td>
                </tr>
              )}
              {data.map((line) => {
                // La profundidad viene del servidor, que la calcula recorriendo
                // el árbol del mapping: ordenar por identificador separaría a
                // los padres de sus hijos.
                const level = line.depth;
                const classes = [`level-${Math.min(level, 2)}`];
                if (line.origin === "correction") classes.push("correction");
                return (
                  <tr key={line.line_id} className={classes.join(" ")}>
                    <td className="mono">{line.line_id}</td>
                    <td style={{ paddingLeft: 12 + level * 16 }}>{line.label}</td>
                    <td className={moneyClass(line.period_balance)}>{formatMoney(line.period_balance)}</td>
                    <td className={moneyClass(line.closing_balance)}>{formatMoney(line.closing_balance)}</td>
                    <td className="small">
                      <Link to={`/audit?line=${line.line_id}`}>composición</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
